import { useEffect, useMemo, useState } from "react";
import { PartyPopper, Loader2, Send, Clock, Eye, ImagePlus, X, Users } from "lucide-react";
import { api, errText, photoUrl } from "../../lib/api";
import { HERO_TEMPLATES, nextWindow, rollForward } from "../../lib/heroTemplates";
import { torontoNow } from "../../lib/useSlideRotation";
import { PROVINCES } from "../../lib/constants";
import { useAuth } from "../../context/AuthContext";
import { FlaskConical, MapPin } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";

const EMPTY = { subject: "", subject_fr: "", message: "", message_fr: "", cta_label: "", cta_label_fr: "", cta_url: "", image: "" };
const torontoToISO = (v) => {
  const guess = new Date(`${v}:00Z`);
  const tz = new Date(guess.toLocaleString("en-US", { timeZone: "America/Toronto" }));
  const utc = new Date(guess.toLocaleString("en-US", { timeZone: "UTC" }));
  return new Date(guess.getTime() + (utc - tz)).toISOString();
};

// Admin composer for a holiday email to seasonal-news subscribers, pre-filled from a slide template.
export function HolidayEmail({ onSent }) {
  const [mine, setMine] = useState([]);
  const [key, setKey] = useState("");
  const [f, setF] = useState(EMPTY);
  const [lang, setLang] = useState("en");
  const [scheduleOn, setScheduleOn] = useState(true);
  const [when, setWhen] = useState("");
  const [audience, setAudience] = useState(null);
  const [busy, setBusy] = useState(false);
  const [genImg, setGenImg] = useState(false);
  const { user } = useAuth();
  const [provs, setProvs] = useState([]);
  const [testTo, setTestTo] = useState(user?.email || "");
  const [testLang, setTestLang] = useState("en");
  const [testing, setTesting] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggleProv = (c) => setProvs((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));

  useEffect(() => { api.get("/admin/hero-templates").then((r) => setMine(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    let stale = false;
    api.get(`/admin/seasonal-email/audience?provinces=${provs.join(",")}`).then((r) => { if (!stale) setAudience(r.data); }).catch(() => {});
    return () => { stale = true; };
  }, [provs]);

  const sendTest = async () => {
    if (!f.subject.trim() || !f.message.trim()) return toast.error("Add a subject and a message");
    setTesting(true);
    try {
      await api.post("/admin/seasonal-email/test", { ...f, to: testTo, lang: testLang });
      toast.success(`Test sent to ${testTo}`);
    } catch (e) { toast.error(errText(e)); }
    finally { setTesting(false); }
  };

  const options = useMemo(() => [
    ...HERO_TEMPLATES.map((t) => ({ id: `b:${t.key}`, name: t.name, theme: t.key, t, win: nextWindow(t.rule) })),
    ...mine.map((m) => ({ id: `m:${m.id}`, name: `${m.name} (saved)`, theme: "default", t: m.slide, win: rollForward(m.slide.start_at, m.slide.end_at) })),
  ], [mine]);

  const choose = async (id) => {
    setKey(id);
    const o = options.find((x) => x.id === id);
    if (!o) return;
    const { t, win } = o;
    const b = (t.buttons || [])[0] || {};
    setF({
      subject: `${t.title_line1} ${t.title_line2}`.trim(), subject_fr: `${t.title_line1_fr || ""} ${t.title_line2_fr || ""}`.trim(),
      message: t.subtitle || "", message_fr: t.subtitle_fr || "", cta_label: b.label || "", cta_label_fr: b.label_fr || "", cta_url: b.link || "", image: "",
    });
    const now = torontoNow();
    let at = win.start_at ? `${win.start_at.slice(0, 10)}T09:00` : "";
    if (at && at <= now) {
      const d = new Date(`${now.slice(0, 10)}T12:00:00Z`);
      if (now.slice(11) >= "09:00") d.setUTCDate(d.getUTCDate() + 1);
      at = `${d.toISOString().slice(0, 10)}T09:00`;
    }
    setWhen(at);
    setScheduleOn(!!win.start_at);
    if (o.theme === "fete-nationale") setProvs(["QC"]);
    setGenImg(true);
    try {
      const { data } = await api.post("/admin/seasonal-banner", { theme: o.theme, badge: t.badge, title_line1: t.title_line1, title_line2: t.title_line2, button_label: b.label || "", tall: true });
      setF((x) => ({ ...x, image: data.image }));
    } catch (e) { toast.error(errText(e)); }
    finally { setGenImg(false); }
  };

  const send = async () => {
    if (!f.subject.trim() || !f.message.trim()) return toast.error("Add a subject and a message");
    setBusy(true);
    try {
      const name = options.find((x) => x.id === key)?.name || "";
      const { data } = await api.post("/admin/seasonal-email", { ...f, template_name: name, provinces: provs, scheduled_at: scheduleOn && when ? torontoToISO(when) : "" });
      toast.success(data.status === "scheduled" ? `Holiday email scheduled for ${new Date(data.scheduled_at).toLocaleString("en-CA", { timeZone: "America/Toronto", dateStyle: "medium", timeStyle: "short" })} Toronto time` : `Holiday email sending to ${data.sent} subscribers`);
      setF(EMPTY); setKey(""); setWhen(""); setScheduleOn(true); setLang("en"); setProvs([]);
      onSent?.();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };

  const p = (k) => (lang === "fr" ? f[`${k}_fr`] || f[k] : f[k]);
  return (
    <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200/80 p-5" data-testid="holiday-email">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2"><PartyPopper size={18} className="text-[#F5551E]" /> Holiday email</h3>
          <p className="text-xs text-slate-500 mt-0.5">Email-only blast to members with "Seasonal news & holiday tips" on. Each member gets English or French, with a one-click unsubscribe link.</p>
        </div>
        {audience && <span className="text-xs font-semibold text-[#12234A] bg-slate-100 rounded-full px-3 py-1 flex items-center gap-1.5" data-testid="holiday-email-audience"><Users size={13} /> {audience.total} subscriber{audience.total === 1 ? "" : "s"}{provs.length ? ` in ${provs.join(", ")}` : ""} · {audience.fr} FR</span>}
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mt-4">
        <div className="space-y-3">
          <div>
            <Label>Template</Label>
            <select value={key} onChange={(e) => choose(e.target.value)} data-testid="holiday-email-template" className="mt-1 w-full h-10 rounded-xl border border-slate-200 px-3 text-sm bg-white">
              <option value="">Choose a holiday template…</option>
              {options.map((o) => <option key={o.id} value={o.id}>{o.name}{o.win.start_at ? ` · starts ${o.win.start_at.slice(0, 10)}` : ""}</option>)}
            </select>
          </div>
          {[["subject", "Subject"], ["message", "Message"], ["cta_label", "Button label"]].map(([k, lbl]) => (
            <div key={k} className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">{lbl}</Label>
                {k === "message"
                  ? <Textarea value={f[k]} onChange={(e) => set(k, e.target.value)} rows={4} data-testid={`holiday-email-${k}`} className="mt-1 rounded-xl text-sm" />
                  : <Input value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`holiday-email-${k}`} className="mt-1 h-9 rounded-xl text-sm" />}
              </div>
              <div>
                <Label className="text-xs">{lbl} (FR)</Label>
                {k === "message"
                  ? <Textarea value={f[`${k}_fr`]} onChange={(e) => set(`${k}_fr`, e.target.value)} rows={4} data-testid={`holiday-email-${k}-fr`} className="mt-1 rounded-xl text-sm" />
                  : <Input value={f[`${k}_fr`]} onChange={(e) => set(`${k}_fr`, e.target.value)} data-testid={`holiday-email-${k}-fr`} className="mt-1 h-9 rounded-xl text-sm" />}
              </div>
            </div>
          ))}
          <div>
            <Label className="text-xs">Button link</Label>
            <Input value={f.cta_url} onChange={(e) => set("cta_url", e.target.value)} placeholder="/browse?category=rescue" data-testid="holiday-email-cta-url" className="mt-1 h-9 rounded-xl text-sm" />
          </div>
          <div data-testid="holiday-email-provinces">
            <Label className="text-xs flex items-center gap-1"><MapPin size={12} /> Provinces</Label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              <button type="button" onClick={() => setProvs([])} data-testid="holiday-email-prov-all"
                className={`h-7 px-2.5 rounded-lg text-xs font-semibold border transition-colors ${!provs.length ? "bg-[#12234A] text-white border-[#12234A]" : "border-slate-200 text-slate-600 hover:border-[#F5551E]"}`}>All provinces</button>
              {PROVINCES.filter((x) => x.code !== "all").map((x) => (
                <button type="button" key={x.code} onClick={() => toggleProv(x.code)} title={x.name} data-testid={`holiday-email-prov-${x.code}`}
                  className={`h-7 px-2.5 rounded-lg text-xs font-semibold border transition-colors ${provs.includes(x.code) ? "bg-[#F5551E] text-white border-[#F5551E]" : "border-slate-200 text-slate-600 hover:border-[#F5551E]"}`}>{x.code}</button>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-1">{provs.length ? "Only members whose profile (or latest listing) is in these provinces." : "Everyone, including members with no known province."}</p>
          </div>
          <div className="rounded-xl border border-dashed border-slate-300 p-3" data-testid="holiday-email-test">
            <Label className="text-xs flex items-center gap-1"><FlaskConical size={12} /> Send a test first</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" data-testid="holiday-email-test-to" className="h-9 rounded-xl text-sm flex-1 min-w-[180px]" />
              <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5 h-9 items-center">
                {["en", "fr"].map((l) => <button type="button" key={l} onClick={() => setTestLang(l)} data-testid={`holiday-email-test-lang-${l}`} className={`text-xs font-semibold px-2.5 py-1 rounded-md ${testLang === l ? "bg-white shadow-sm text-[#12234A]" : "text-slate-500"}`}>{l.toUpperCase()}</button>)}
              </div>
              <Button variant="outline" onClick={sendTest} disabled={testing || genImg || !testTo.trim()} data-testid="holiday-email-test-send" className="h-9 rounded-xl text-sm gap-1.5">
                {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test
              </Button>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">Subject is marked [TEST]. Nothing is sent to members.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <label className="flex items-center gap-2 text-sm text-[#12234A]"><Switch checked={scheduleOn} onCheckedChange={setScheduleOn} data-testid="holiday-email-schedule-toggle" /> Schedule (Toronto time)</label>
            {scheduleOn && <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="holiday-email-when" className="h-9 w-56 rounded-xl text-sm" />}
          </div>
          <Button onClick={send} disabled={busy || genImg || (scheduleOn && !when)} data-testid="holiday-email-send" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">
            {busy ? <Loader2 size={16} className="animate-spin" /> : scheduleOn ? <Clock size={16} /> : <Send size={16} />} {scheduleOn ? "Schedule holiday email" : `Send now to ${audience?.total ?? 0}`}
          </Button>
        </div>

        <div data-testid="holiday-email-preview">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Eye size={13} /> Preview</p>
            <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
              {["en", "fr"].map((l) => <button key={l} onClick={() => setLang(l)} data-testid={`holiday-email-preview-${l}`} className={`text-xs font-semibold px-2.5 py-1 rounded-md ${lang === l ? "bg-white shadow-sm text-[#12234A]" : "text-slate-500"}`}>{l.toUpperCase()}</button>)}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs text-slate-500 mb-2"><b>Subject:</b> <span data-testid="holiday-email-preview-subject">{p("subject") || "—"}</span></p>
            <div className="bg-white rounded-xl p-4">
              <div className="relative">
                {genImg ? <div className="aspect-[6/1] rounded-lg bg-slate-100 flex items-center justify-center"><Loader2 size={18} className="animate-spin text-slate-400" /></div>
                  : f.image ? (
                    <>
                      <img src={photoUrl(f.image)} alt="" className="w-full rounded-lg" data-testid="holiday-email-preview-image" />
                      <button onClick={() => set("image", "")} data-testid="holiday-email-image-remove" className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center"><X size={12} /></button>
                    </>
                  ) : <div className="aspect-[6/1] rounded-lg border-2 border-dashed border-slate-200 flex items-center justify-center text-xs text-slate-400 gap-1"><ImagePlus size={14} /> Pick a template to generate the banner</div>}
              </div>
              <p className="text-sm text-[#12234A] mt-3">{lang === "fr" ? "Bonjour" : "Hi"} Jane,</p>
              <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{p("message") || "Your message…"}</p>
              {f.cta_url && p("cta_label") && <div className="text-center mt-3"><span className="inline-block bg-[#F5551E] text-white text-sm font-semibold px-5 py-2 rounded-lg">{p("cta_label")}</span></div>}
              <p className="text-[10px] text-slate-400 mt-4">{lang === "fr" ? "Se désabonner en un clic" : "Unsubscribe in one click"}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
